import { useReportStateContext } from "../hooks/useReportState";
import { FilterKindOption } from "./FilterKindOption";
import {
  ArrowDownward as SortDescIcon,
  ArrowUpward as SortAscIcon,
  BarChart as MetricsIcon,
  Check as CheckIcon,
  Clear as ClearSortIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  Numbers as FormatIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
  VisibilityOff as HideIcon,
  Summarize as SummaryTotalIcon,
  Workspaces as GroupByIcon,
} from "@mui/icons-material";
import {
  ClickAwayListener,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import {
  DATE_AGGREGATE_METHODS,
  FILTER_KIND_OUTPUT,
  HEADER_MENU_ICON_MIN_WIDTH_PX,
  HEADER_MENU_MIN_WIDTH_PX,
  HEADER_MENU_Z_INDEX,
  HEADER_SUBMENU_CLOSE_DELAY_MS,
  HEADER_SUBMENU_MIN_WIDTH_PX,
  HEADER_SUBMENU_Z_INDEX,
  HEADER_SUMMARY_SUBMENU_MIN_WIDTH_PX,
  NUMERIC_AGGREGATE_METHODS,
  STRING_COLUMN_TYPE,
  SUBMENU_INDICATOR,
} from "components/reportsV3/types/constants";
import { useGroupAggregators } from "components/reportsV3/utils/aggregationUtils";
import { filterKindLabels as labelsOfFilterKinds } from "components/reportsV3/utils/filterKindUtils";
import * as labels from "config.labels";
import { ENABLE_AGGREGATE_COLUMN_GENERATION } from "flags";
import React, { useCallback, useEffect, useRef, useState } from "react";
import * as staticDataService from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";

// Other props go through to the MenuItem: MenuList hands its first item
// autoFocus and tabIndex, and without them the menu opens with focus left
// behind on the column header.
const SimpleMenuItem = ({
  disabled = false,
  onClick,
  Icon,
  text,
  adornment = null,
  tooltip,
  ...menuItemProps
}) => {
  return (
    <MenuItem
      {...menuItemProps}
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      sx={{ pr: "0.875rem" }}
    >
      <ListItemIcon>
        <Icon fontSize={"small"} />
      </ListItemIcon>
      <ListItemText
        primary={text}
        slotProps={{
          primary: {
            sx: {
              fontSize: "0.875rem !important",
              fontWeight: 500,
              paddingRight: "0.875rem",
            },
          },
        }}
      />
      {adornment}
    </MenuItem>
  );
};

const DEFAULT_FILTER_KINDS = [FILTER_KIND_OUTPUT];
const NO_FILTER_CHIPS = {};
// Keys the summary text row lets through to the menu list, so keyboard users
// can move past it; any other key moves focus into its text box.
const MENU_NAVIGATION_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "Home",
  "End",
  "Escape",
  "Tab",
]);

export const ColumnHeaderMenu = ({
  open,
  anchorEl,
  fullscreenContainerRef,
  menuColumn,
  onClose,
  onSortAsc,
  onSortDesc,
  onClearSort,
  isMenuColumnSorted,
  onFilterColumn,
  filterKinds = DEFAULT_FILTER_KINDS,
  filterChipsByKind = NO_FILTER_CHIPS,
  groupingActive = false,
  onRenameColumn,
  onPinColumn,
  onUnpinColumn,
  onHideColumn,
  onAddToMetrics,
  onRemoveFromMetrics,
  isInMetrics,
  columnMetricMethods,
  columnType,
  columnSubType,
  columnDecimals,
  onSetColumnDecimals,
  isGroupedBy,
  onGroupByColumn,
  onUngroupColumn,
  isRangeGrouped,
  onRangeGroupBy,
  onEditRangeGrouping,
  onUngroupRangeGrouping,
  summarySelection,
  onSetSummaryAggregator,
  onSetSummaryOverride,
  onClearSummary,
}) => {
  const aggregationEnabled = staticDataService.useFeatureFlag(
    ENABLE_AGGREGATE_COLUMN_GENERATION,
    { default: true }
  );
  const { pinnedColumns } = useReportStateContext();
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const aggregatorUtils = useGroupAggregators();
  const [subMenuAnchor, setSubMenuAnchor] = useState(null);
  const subMenuRef = useRef(null);
  const closeTimerRef = useRef(null);

  const [summaryAnchor, setSummaryAnchor] = useState(null);
  const summaryRef = useRef(null);
  const summaryCloseTimerRef = useRef(null);
  const [overrideText, setOverrideText] = useState("");

  const [formatAnchor, setFormatAnchor] = useState(null);
  const formatRef = useRef(null);
  const formatCloseTimerRef = useRef(null);

  const [rangeGroupAnchor, setRangeGroupAnchor] = useState(null);
  const rangeGroupRef = useRef(null);
  const rangeGroupCloseTimerRef = useRef(null);

  const [filterKindAnchor, setFilterKindAnchor] = useState(null);
  const filterKindRef = useRef(null);
  const filterKindCloseTimerRef = useRef(null);

  // The submenu opened by click or keyboard, whose first item takes focus
  // (MenuList's autoFocusItem). Hovering an item that opens a submenu clears
  // it, so a submenu opened by hover leaves focus where it is.
  const [focusedSubmenu, setFocusedSubmenu] = useState(null);
  const overrideRowRef = useRef(null);
  const overrideInputRef = useRef(null);
  const savedOverrideRef = useRef("");

  const filterKindLabels = labelsOfFilterKinds(translateLabels, groupingActive);

  const isNumericColumn = columnType === "INT" || columnType === "FLOAT";
  const isFloatColumn = columnType === "FLOAT";
  const isDateColumn = columnType === "DATE";
  const isStringColumn = columnType === STRING_COLUMN_TYPE;
  const offersRangeChoice =
    (columnType === "INT" || isDateColumn || isStringColumn) &&
    !isGroupedBy &&
    !isRangeGrouped;
  const hasSubMenu = isNumericColumn || isDateColumn;
  const subMenuMethods = isNumericColumn
    ? NUMERIC_AGGREGATE_METHODS
    : DATE_AGGREGATE_METHODS;

  const summaryAggregationOptions =
    aggregatorUtils?.getAggregationOptions(columnType) ?? [];
  const summaryFunc = summarySelection?.func ?? null;

  // Keep the override text box in sync with the active selection whenever the
  // Summary Total submenu is opened.
  useEffect(() => {
    if (summaryAnchor) {
      setOverrideText(summarySelection?.override ?? "");
      savedOverrideRef.current = (summarySelection?.override ?? "").trim();
    }
  }, [summaryAnchor, summarySelection]);

  const handleSummaryMouseEnter = useCallback((event) => {
    clearTimeout(summaryCloseTimerRef.current);
    setSummaryAnchor(event.currentTarget);
  }, []);

  const handleSummaryMouseLeave = useCallback(() => {
    summaryCloseTimerRef.current = setTimeout(
      () => setSummaryAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  const handleSummarySubMenuMouseEnter = useCallback(() => {
    clearTimeout(summaryCloseTimerRef.current);
  }, []);

  const handleSummarySubMenuMouseLeave = useCallback(() => {
    summaryCloseTimerRef.current = setTimeout(
      () => setSummaryAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  const handleFormatMouseEnter = useCallback((event) => {
    clearTimeout(formatCloseTimerRef.current);
    setFormatAnchor(event.currentTarget);
  }, []);

  const handleFormatMouseLeave = useCallback(() => {
    formatCloseTimerRef.current = setTimeout(
      () => setFormatAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  const handleFormatSubMenuMouseEnter = useCallback(() => {
    clearTimeout(formatCloseTimerRef.current);
  }, []);

  const handleFormatSubMenuMouseLeave = useCallback(() => {
    formatCloseTimerRef.current = setTimeout(
      () => setFormatAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  const handleRangeGroupMouseEnter = useCallback((event) => {
    clearTimeout(rangeGroupCloseTimerRef.current);
    setRangeGroupAnchor(event.currentTarget);
  }, []);

  const handleRangeGroupMouseLeave = useCallback(() => {
    rangeGroupCloseTimerRef.current = setTimeout(
      () => setRangeGroupAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  const handleRangeGroupSubMenuMouseEnter = useCallback(() => {
    clearTimeout(rangeGroupCloseTimerRef.current);
  }, []);

  const handleRangeGroupSubMenuMouseLeave = useCallback(() => {
    rangeGroupCloseTimerRef.current = setTimeout(
      () => setRangeGroupAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  const handleFilterKindMouseEnter = useCallback((event) => {
    clearTimeout(filterKindCloseTimerRef.current);
    setFilterKindAnchor(event.currentTarget);
  }, []);

  const handleFilterKindMouseLeave = useCallback(() => {
    filterKindCloseTimerRef.current = setTimeout(
      () => setFilterKindAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  const handleFilterKindSubMenuMouseEnter = useCallback(() => {
    clearTimeout(filterKindCloseTimerRef.current);
  }, []);

  const handleFilterKindSubMenuMouseLeave = useCallback(() => {
    filterKindCloseTimerRef.current = setTimeout(
      () => setFilterKindAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  // Saves the text only when it has changed, so passing through the box from
  // the keyboard keeps the chosen function, and Enter followed by the box
  // losing focus saves once.
  const commitOverride = useCallback(() => {
    if (!menuColumn) return;
    const trimmed = overrideText.trim();
    if (trimmed === savedOverrideRef.current) return;
    savedOverrideRef.current = trimmed;
    if (trimmed) {
      onSetSummaryOverride?.(menuColumn, trimmed);
    } else {
      onClearSummary?.(menuColumn);
    }
  }, [menuColumn, overrideText, onSetSummaryOverride, onClearSummary]);

  const onTogglePinColumn = () => {
    if (!menuColumn) return;

    if (pinnedColumns.includes(menuColumn)) {
      onUnpinColumn(menuColumn);
    } else {
      onPinColumn(menuColumn);
    }
    onClose();
  };

  const handleMetricsMouseEnter = useCallback((event) => {
    clearTimeout(closeTimerRef.current);
    setSubMenuAnchor(event.currentTarget);
  }, []);

  const handleMetricsMouseLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(
      () => setSubMenuAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  const handleSubMenuMouseEnter = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  const handleSubMenuMouseLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(
      () => setSubMenuAnchor(null),
      HEADER_SUBMENU_CLOSE_DELAY_MS
    );
  }, []);

  const handleToggleAggregation = useCallback(
    (method) => {
      if (!menuColumn) return;
      if (columnMetricMethods?.has(method)) {
        onRemoveFromMetrics(menuColumn, method);
      } else {
        onAddToMetrics(menuColumn, method);
      }
    },
    [menuColumn, columnMetricMethods, onAddToMetrics, onRemoveFromMetrics]
  );

  const handleCloseAll = useCallback(() => {
    setSubMenuAnchor(null);
    setSummaryAnchor(null);
    setFormatAnchor(null);
    setRangeGroupAnchor(null);
    setFilterKindAnchor(null);
    setFocusedSubmenu(null);
    onClose();
  }, [onClose]);

  // The submenus keep their own anchor and hover close timer, as two can be
  // open for a moment while the mouse moves from one to the next. This lets
  // the click and keyboard handlers below reach any of them by name.
  const submenus = {
    filterKind: {
      anchor: filterKindAnchor,
      setAnchor: setFilterKindAnchor,
      closeTimerRef: filterKindCloseTimerRef,
    },
    metrics: {
      anchor: subMenuAnchor,
      setAnchor: setSubMenuAnchor,
      closeTimerRef: closeTimerRef,
    },
    rangeGroup: {
      anchor: rangeGroupAnchor,
      setAnchor: setRangeGroupAnchor,
      closeTimerRef: rangeGroupCloseTimerRef,
    },
    format: {
      anchor: formatAnchor,
      setAnchor: setFormatAnchor,
      closeTimerRef: formatCloseTimerRef,
    },
    summary: {
      anchor: summaryAnchor,
      setAnchor: setSummaryAnchor,
      closeTimerRef: summaryCloseTimerRef,
    },
  };

  // Click and keyboard open a submenu with focus on its first item and close
  // any other straight away.
  const openSubmenuWithFocus = (name, parentElement) => {
    Object.entries(submenus).forEach(([key, submenu]) => {
      clearTimeout(submenu.closeTimerRef.current);
      submenu.setAnchor(key === name ? parentElement : null);
    });
    setFocusedSubmenu(name);
  };

  // Hover gives any other open submenu the usual delay before it closes, as
  // moving off it does, so crossing a neighbour on the way into a submenu
  // doesn't shut it. This also closes a submenu opened from the keyboard,
  // which has no close pending of its own.
  const closeOtherSubmenusSoon = (name) => {
    Object.entries(submenus).forEach(([key, submenu]) => {
      if (key === name || !submenu.anchor) return;
      clearTimeout(submenu.closeTimerRef.current);
      submenu.closeTimerRef.current = setTimeout(
        () => submenu.setAnchor(null),
        HEADER_SUBMENU_CLOSE_DELAY_MS
      );
    });
  };

  // Props for a menu item that opens a submenu: hover still works for mouse
  // users; click, Enter, Space and ArrowRight open it and focus its first item.
  const submenuParentProps = (name, onMouseEnter, onMouseLeave) => ({
    onMouseEnter: (event) => {
      closeOtherSubmenusSoon(name);
      setFocusedSubmenu(null);
      onMouseEnter(event);
    },
    onMouseLeave,
    onClick: (event) => openSubmenuWithFocus(name, event.currentTarget),
    onKeyDown: (event) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        event.stopPropagation();
        openSubmenuWithFocus(name, event.currentTarget);
      }
    },
    "aria-haspopup": "menu",
    "aria-expanded": Boolean(submenus[name].anchor),
    "data-testid": `column-header-menu-${name}`,
  });

  // ArrowLeft and Escape close a submenu and return focus to its parent item;
  // Tab closes the whole menu.
  const submenuListKeyDown = (name) => (event) => {
    if (event.key === "ArrowLeft" || event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      const parentElement = submenus[name].anchor;
      clearTimeout(submenus[name].closeTimerRef.current);
      submenus[name].setAnchor(null);
      setFocusedSubmenu(null);
      parentElement?.focus();
    } else if (event.key === "Tab") {
      event.preventDefault();
      handleCloseAll();
    }
  };

  // As in MUI's Menu: Escape and Tab close the menu rather than letting focus
  // wander into the page behind it. Focus then goes back to the header.
  const handleMenuListKeyDown = (event) => {
    if (event.key === "Escape" || event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();
      handleCloseAll();
    }
  };

  const activeDecimals = menuColumn ? columnDecimals?.[menuColumn] : undefined;
  const formatOptions = [
    {
      key: "auto",
      label: translateLabels(labels.COLUMN_HEADER_MENU_FORMAT_AUTO, {
        default: "Auto",
      }),
      value: null,
      isActive: activeDecimals === undefined || activeDecimals === null,
    },
    ...[0, 1, 2, 3].map((count) => ({
      key: String(count),
      label: String(count),
      value: count,
      isActive: activeDecimals === count,
    })),
  ];

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={"bottom-start"}
      container={fullscreenContainerRef.current}
      sx={{
        zIndex: HEADER_MENU_Z_INDEX,
      }}
    >
      <ClickAwayListener onClickAway={handleCloseAll}>
        <Paper
          elevation={3}
          sx={{
            minWidth: HEADER_MENU_MIN_WIDTH_PX,
          }}
        >
          <MenuList autoFocusItem={open} onKeyDown={handleMenuListKeyDown}>
            <SimpleMenuItem
              onClick={onSortAsc}
              Icon={SortAscIcon}
              text={translateLabels(labels.COLUMN_HEADER_MENU_SORT_ASCENDING, {
                default: "Sort Ascending",
              })}
            />
            <SimpleMenuItem
              onClick={onSortDesc}
              Icon={SortDescIcon}
              text={translateLabels(labels.COLUMN_HEADER_MENU_SORT_DESCENDING, {
                default: "Sort Descending",
              })}
            />
            {isMenuColumnSorted ? (
              <SimpleMenuItem
                onClick={onClearSort}
                Icon={ClearSortIcon}
                text={translateLabels(labels.COLUMN_HEADER_MENU_CLEAR_SORTING, {
                  default: "Clear Sorting",
                })}
              />
            ) : null}
            {filterKinds.length > 1 ? (
              <MenuItem
                {...submenuParentProps(
                  "filterKind",
                  handleFilterKindMouseEnter,
                  handleFilterKindMouseLeave
                )}
                sx={{ pr: "0.875rem" }}
              >
                <ListItemIcon>
                  <FilterListIcon fontSize={"small"} />
                </ListItemIcon>
                <ListItemText
                  primary={translateLabels(labels.COLUMN_HEADER_MENU_FILTER, {
                    default: "Filter",
                  })}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "0.875rem !important",
                        fontWeight: 500,
                        paddingRight: "0.875rem",
                      },
                    },
                  }}
                />
                {SUBMENU_INDICATOR}
              </MenuItem>
            ) : (
              <SimpleMenuItem
                onClick={() => onFilterColumn?.(filterKinds[0])}
                Icon={FilterListIcon}
                text={
                  <FilterKindOption
                    label={filterKindLabels[filterKinds[0]]}
                    chips={filterChipsByKind[filterKinds[0]]}
                  />
                }
              />
            )}
            <Divider />
            <SimpleMenuItem
              onClick={onRenameColumn}
              Icon={EditIcon}
              text={translateLabels(labels.COLUMN_HEADER_MENU_RENAME, {
                default: "Rename",
              })}
            />
            <SimpleMenuItem
              onClick={onTogglePinColumn}
              Icon={
                pinnedColumns.includes(menuColumn ?? "")
                  ? PushPinOutlinedIcon
                  : PushPinIcon
              }
              text={
                pinnedColumns.includes(menuColumn ?? "")
                  ? translateLabels(labels.COLUMN_HEADER_MENU_UNPIN, {
                      default: "Unpin",
                    })
                  : translateLabels(labels.COLUMN_HEADER_MENU_PIN, {
                      default: "Pin",
                    })
              }
            />
            <SimpleMenuItem
              onClick={() => {
                if (menuColumn) {
                  onHideColumn(menuColumn);
                  onClose();
                }
              }}
              Icon={HideIcon}
              text={translateLabels(labels.COLUMN_HEADER_MENU_REMOVE, {
                default: "Hide",
              })}
            />
            <Divider />
            {hasSubMenu ? (
              <MenuItem
                {...submenuParentProps(
                  "metrics",
                  handleMetricsMouseEnter,
                  handleMetricsMouseLeave
                )}
                sx={{ pr: "0.875rem" }}
              >
                <ListItemIcon>
                  <MetricsIcon fontSize={"small"} />
                </ListItemIcon>
                <ListItemText
                  primary={translateLabels(
                    labels.COLUMN_HEADER_MENU_ADD_TO_METRICS,
                    { default: "Add to Metrics" }
                  )}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "0.875rem !important",
                        fontWeight: 500,
                        paddingRight: "0.875rem",
                      },
                    },
                  }}
                />
                {"▸"}
              </MenuItem>
            ) : isInMetrics ? (
              <SimpleMenuItem
                onClick={() => {
                  if (menuColumn) {
                    onRemoveFromMetrics(menuColumn);
                    onClose();
                  }
                }}
                Icon={MetricsIcon}
                text={translateLabels(
                  labels.COLUMN_HEADER_MENU_REMOVE_FROM_METRICS,
                  { default: "Remove from Metrics" }
                )}
              />
            ) : (
              <SimpleMenuItem
                onClick={() => {
                  if (menuColumn) {
                    onAddToMetrics(menuColumn);
                    onClose();
                  }
                }}
                Icon={MetricsIcon}
                text={translateLabels(
                  labels.COLUMN_HEADER_MENU_ADD_TO_METRICS,
                  { default: "Add to Metrics" }
                )}
              />
            )}
            {aggregationEnabled && [
              <Divider key={"group-by-divider"} />,
              isRangeGrouped || offersRangeChoice ? (
                <MenuItem
                  key={"range-group-by"}
                  {...submenuParentProps(
                    "rangeGroup",
                    handleRangeGroupMouseEnter,
                    handleRangeGroupMouseLeave
                  )}
                  sx={{ pr: "0.875rem" }}
                >
                  <ListItemIcon>
                    <GroupByIcon fontSize={"small"} />
                  </ListItemIcon>
                  <ListItemText
                    primary={translateLabels(
                      labels.COLUMN_HEADER_MENU_GROUP_BY,
                      {
                        default: "Group by",
                      }
                    )}
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: "0.875rem !important",
                          fontWeight: 500,
                          paddingRight: "0.875rem",
                        },
                      },
                    }}
                  />
                  {"▸"}
                </MenuItem>
              ) : isFloatColumn ? (
                <SimpleMenuItem
                  key={"range-group-by"}
                  onClick={() => {
                    if (!menuColumn) return;
                    onRangeGroupBy?.(menuColumn);
                    onClose();
                  }}
                  Icon={GroupByIcon}
                  text={translateLabels(labels.COLUMN_HEADER_MENU_GROUP_BY, {
                    default: "Group by",
                  })}
                />
              ) : (
                <SimpleMenuItem
                  key={"group-by"}
                  onClick={() => {
                    if (!menuColumn) return;
                    if (isGroupedBy) {
                      onUngroupColumn?.(menuColumn);
                    } else {
                      onGroupByColumn?.(menuColumn);
                    }
                    onClose();
                  }}
                  Icon={GroupByIcon}
                  text={
                    isGroupedBy
                      ? translateLabels(labels.COLUMN_HEADER_MENU_UNGROUP, {
                          default: "Ungroup",
                        })
                      : translateLabels(labels.COLUMN_HEADER_MENU_GROUP_BY, {
                          default: "Group by",
                        })
                  }
                />
              ),
            ]}
            <Divider />
            {isFloatColumn && (
              <MenuItem
                {...submenuParentProps(
                  "format",
                  handleFormatMouseEnter,
                  handleFormatMouseLeave
                )}
                sx={{ pr: "0.875rem" }}
              >
                <ListItemIcon>
                  <FormatIcon fontSize={"small"} />
                </ListItemIcon>
                <ListItemText
                  primary={translateLabels(labels.COLUMN_HEADER_MENU_FORMAT, {
                    default: "Decimal Places",
                  })}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "0.875rem !important",
                        fontWeight: 500,
                        paddingRight: "0.875rem",
                      },
                    },
                  }}
                />
                {activeDecimals !== undefined && activeDecimals !== null ? (
                  <CheckIcon fontSize={"small"} color={"primary"} />
                ) : null}
                {"▸"}
              </MenuItem>
            )}
            <MenuItem
              {...submenuParentProps(
                "summary",
                handleSummaryMouseEnter,
                handleSummaryMouseLeave
              )}
              sx={{ pr: "0.875rem" }}
            >
              <ListItemIcon>
                <SummaryTotalIcon fontSize={"small"} />
              </ListItemIcon>
              <ListItemText
                primary={translateLabels(
                  labels.COLUMN_HEADER_MENU_SUMMARY_TOTAL,
                  { default: "Summary Function" }
                )}
                slotProps={{
                  primary: {
                    sx: {
                      fontSize: "0.875rem !important",
                      fontWeight: 500,
                      paddingRight: "0.875rem",
                    },
                  },
                }}
              />
              {summarySelection ? (
                <CheckIcon fontSize={"small"} color={"primary"} />
              ) : null}
              {"▸"}
            </MenuItem>
          </MenuList>
        </Paper>
      </ClickAwayListener>
      <Popper
        open={Boolean(filterKindAnchor)}
        anchorEl={filterKindAnchor}
        placement={"right-start"}
        container={fullscreenContainerRef.current}
        sx={{ zIndex: HEADER_SUBMENU_Z_INDEX }}
      >
        <Paper
          ref={filterKindRef}
          elevation={4}
          sx={{ minWidth: HEADER_SUBMENU_MIN_WIDTH_PX }}
          onMouseEnter={handleFilterKindSubMenuMouseEnter}
          onMouseLeave={handleFilterKindSubMenuMouseLeave}
        >
          <MenuList
            autoFocusItem={focusedSubmenu === "filterKind"}
            onKeyDown={submenuListKeyDown("filterKind")}
          >
            {filterKinds.map((kind) => (
              <MenuItem
                key={kind}
                onClick={() => {
                  onFilterColumn?.(kind);
                  handleCloseAll();
                }}
                sx={{ pr: "1rem" }}
              >
                <ListItemText
                  primary={
                    <FilterKindOption
                      label={filterKindLabels[kind]}
                      chips={filterChipsByKind[kind]}
                    />
                  }
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "0.875rem !important",
                        fontWeight: 500,
                      },
                    },
                  }}
                />
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>
      <Popper
        open={Boolean(subMenuAnchor)}
        anchorEl={subMenuAnchor}
        placement={"right-start"}
        container={fullscreenContainerRef.current}
        sx={{ zIndex: HEADER_SUBMENU_Z_INDEX }}
      >
        <Paper
          ref={subMenuRef}
          elevation={4}
          sx={{ minWidth: HEADER_SUBMENU_MIN_WIDTH_PX }}
          onMouseEnter={handleSubMenuMouseEnter}
          onMouseLeave={handleSubMenuMouseLeave}
        >
          <MenuList
            autoFocusItem={focusedSubmenu === "metrics"}
            onKeyDown={submenuListKeyDown("metrics")}
          >
            {subMenuMethods.map((method) => {
              const isActive = columnMetricMethods?.has(method);
              return (
                <MenuItem
                  key={method}
                  onClick={() => handleToggleAggregation(method)}
                  sx={{ pr: "1rem" }}
                >
                  <ListItemIcon
                    sx={{ minWidth: HEADER_MENU_ICON_MIN_WIDTH_PX }}
                  >
                    {isActive ? (
                      <CheckIcon fontSize={"small"} color={"primary"} />
                    ) : null}
                  </ListItemIcon>
                  <ListItemText
                    primary={method}
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: "0.875rem !important",
                          fontWeight: 500,
                        },
                      },
                    }}
                  />
                </MenuItem>
              );
            })}
          </MenuList>
        </Paper>
      </Popper>
      <Popper
        open={Boolean(summaryAnchor)}
        anchorEl={summaryAnchor}
        placement={"right-start"}
        container={fullscreenContainerRef.current}
        sx={{ zIndex: HEADER_SUBMENU_Z_INDEX }}
      >
        <Paper
          ref={summaryRef}
          elevation={4}
          sx={{ minWidth: HEADER_SUMMARY_SUBMENU_MIN_WIDTH_PX }}
          onMouseEnter={handleSummarySubMenuMouseEnter}
          onMouseLeave={handleSummarySubMenuMouseLeave}
        >
          <MenuList
            autoFocusItem={focusedSubmenu === "summary"}
            onKeyDown={submenuListKeyDown("summary")}
          >
            <MenuItem
              onClick={() => {
                if (menuColumn) onClearSummary?.(menuColumn);
                handleCloseAll();
              }}
              sx={{ pr: "1rem" }}
            >
              <ListItemIcon sx={{ minWidth: HEADER_MENU_ICON_MIN_WIDTH_PX }}>
                <ClearSortIcon fontSize={"small"} />
              </ListItemIcon>
              <ListItemText
                primary={translateLabels(labels.SUMMARY_TOTAL_CLEAR, {
                  default: "Clear",
                })}
                slotProps={{
                  primary: {
                    sx: { fontSize: "0.875rem !important", fontWeight: 500 },
                  },
                }}
              />
            </MenuItem>
            <MenuItem
              disableRipple
              ref={overrideRowRef}
              data-testid={"column-header-menu-summary-text"}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (MENU_NAVIGATION_KEYS.has(e.key)) return;
                e.stopPropagation();
                overrideInputRef.current?.focus();
              }}
              sx={{ pr: "1rem", "&:hover": { backgroundColor: "transparent" } }}
            >
              <TextField
                size={"small"}
                fullWidth
                variant={"standard"}
                inputRef={overrideInputRef}
                placeholder={translateLabels(
                  labels.SUMMARY_TOTAL_TEXT_PLACEHOLDER,
                  {
                    default: "Enter text…",
                  }
                )}
                value={overrideText}
                onChange={(e) => setOverrideText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    e.stopPropagation();
                    commitOverride();
                    handleCloseAll();
                  } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    // Back onto the row, from which the menu list moves on.
                    overrideRowRef.current?.focus();
                  } else if (e.key !== "Escape") {
                    e.stopPropagation();
                  }
                }}
                onBlur={commitOverride}
              />
            </MenuItem>
            <Divider />
            {summaryAggregationOptions.map((option) => {
              const isActive = summaryFunc === option.value;
              return (
                <MenuItem
                  key={option.value}
                  onClick={() => {
                    if (menuColumn) {
                      onSetSummaryAggregator?.(menuColumn, option.value);
                    }
                    handleCloseAll();
                  }}
                  sx={{ pr: "1rem" }}
                >
                  <ListItemIcon
                    sx={{ minWidth: HEADER_MENU_ICON_MIN_WIDTH_PX }}
                  >
                    {isActive ? (
                      <CheckIcon fontSize={"small"} color={"primary"} />
                    ) : null}
                  </ListItemIcon>
                  <ListItemText
                    primary={option.label}
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: "0.875rem !important",
                          fontWeight: 500,
                        },
                      },
                    }}
                  />
                </MenuItem>
              );
            })}
          </MenuList>
        </Paper>
      </Popper>
      <Popper
        open={Boolean(formatAnchor)}
        anchorEl={formatAnchor}
        placement={"right-start"}
        container={fullscreenContainerRef.current}
        sx={{ zIndex: HEADER_SUBMENU_Z_INDEX }}
      >
        <Paper
          ref={formatRef}
          elevation={4}
          sx={{ minWidth: HEADER_SUBMENU_MIN_WIDTH_PX }}
          onMouseEnter={handleFormatSubMenuMouseEnter}
          onMouseLeave={handleFormatSubMenuMouseLeave}
        >
          <MenuList
            autoFocusItem={focusedSubmenu === "format"}
            onKeyDown={submenuListKeyDown("format")}
          >
            {formatOptions.map((option) => (
              <MenuItem
                key={option.key}
                onClick={() => {
                  if (menuColumn) {
                    onSetColumnDecimals?.(menuColumn, option.value);
                  }
                  handleCloseAll();
                }}
                sx={{ pr: "1rem" }}
              >
                <ListItemIcon sx={{ minWidth: HEADER_MENU_ICON_MIN_WIDTH_PX }}>
                  {option.isActive ? (
                    <CheckIcon fontSize={"small"} color={"primary"} />
                  ) : null}
                </ListItemIcon>
                <ListItemText
                  primary={option.label}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: "0.875rem !important",
                        fontWeight: 500,
                      },
                    },
                  }}
                />
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>
      <Popper
        open={Boolean(rangeGroupAnchor)}
        anchorEl={rangeGroupAnchor}
        placement={"right-start"}
        container={fullscreenContainerRef.current}
        sx={{ zIndex: HEADER_SUBMENU_Z_INDEX }}
      >
        <Paper
          ref={rangeGroupRef}
          elevation={4}
          sx={{ minWidth: HEADER_SUBMENU_MIN_WIDTH_PX }}
          onMouseEnter={handleRangeGroupSubMenuMouseEnter}
          onMouseLeave={handleRangeGroupSubMenuMouseLeave}
        >
          <MenuList
            autoFocusItem={focusedSubmenu === "rangeGroup"}
            onKeyDown={submenuListKeyDown("rangeGroup")}
          >
            {isRangeGrouped
              ? [
                  <MenuItem
                    key={"range-ungroup"}
                    onClick={() => {
                      if (menuColumn) onUngroupRangeGrouping?.(menuColumn);
                      handleCloseAll();
                    }}
                    sx={{ pr: "1rem" }}
                  >
                    <ListItemText
                      primary={translateLabels(
                        labels.COLUMN_HEADER_MENU_UNGROUP,
                        {
                          default: "Ungroup",
                        }
                      )}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: "0.875rem !important",
                            fontWeight: 500,
                          },
                        },
                      }}
                    />
                  </MenuItem>,
                  <MenuItem
                    key={"range-edit"}
                    onClick={() => {
                      if (menuColumn) onEditRangeGrouping?.(menuColumn);
                      handleCloseAll();
                    }}
                    sx={{ pr: "1rem" }}
                  >
                    <ListItemText
                      primary={translateLabels(
                        labels.COLUMN_HEADER_MENU_RANGE_GROUPING_EDIT,
                        { default: "Edit" }
                      )}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: "0.875rem !important",
                            fontWeight: 500,
                          },
                        },
                      }}
                    />
                  </MenuItem>,
                ]
              : [
                  <MenuItem
                    key={"range-group"}
                    onClick={() => {
                      if (menuColumn) onGroupByColumn?.(menuColumn);
                      handleCloseAll();
                    }}
                    sx={{ pr: "1rem" }}
                  >
                    <ListItemText
                      primary={translateLabels(
                        labels.COLUMN_HEADER_MENU_RANGE_GROUPING_GROUP,
                        { default: "Group" }
                      )}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: "0.875rem !important",
                            fontWeight: 500,
                          },
                        },
                      }}
                    />
                  </MenuItem>,
                  <MenuItem
                    key={"range-ranges"}
                    onClick={() => {
                      if (menuColumn) onRangeGroupBy?.(menuColumn);
                      handleCloseAll();
                    }}
                    sx={{ pr: "1rem" }}
                  >
                    <ListItemText
                      primary={
                        isStringColumn
                          ? translateLabels(
                              labels.COLUMN_HEADER_MENU_RANGE_GROUPING_MATCHES,
                              { default: "Grouped ranges" }
                            )
                          : translateLabels(
                              labels.COLUMN_HEADER_MENU_RANGE_GROUPING_RANGES,
                              { default: "Ranges" }
                            )
                      }
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: "0.875rem !important",
                            fontWeight: 500,
                          },
                        },
                      }}
                    />
                  </MenuItem>,
                ]}
          </MenuList>
        </Paper>
      </Popper>
    </Popper>
  );
};
export default ColumnHeaderMenu;
